/**
 * Helpers for normalizing catalogue articleIDs entries.
 *
 * Old catalogues store articleIDs as string[].
 * New catalogues store [{id, seasonKey}] to track which season each article was added from.
 * These helpers bridge the two formats.
 */

// Normalize mixed array → consistent {id, seasonKey} entries.
// Old string entries get fallbackSeasonKey (catalogue.seasonKey).
export const normalizeArticleEntries = (articleIDs, fallbackSeasonKey) => {
  if (!articleIDs) return [];
  return articleIDs.map((entry) =>
    typeof entry === "string"
      ? { id: entry, seasonKey: fallbackSeasonKey || "" }
      : entry
  );
};

// Extract unique article IDs for Typesense lookup.
export const extractArticleIDs = (entries) => {
  return [...new Set(entries.map((e) => (typeof e === "string" ? e : e.id)))];
};

// Build Map<articleId, seasonKey> for quick season lookup.
export const buildSeasonMap = (entries) => {
  const map = new Map();
  entries.forEach((e) => {
    if (typeof e === "string") return;
    map.set(e.id, e.seasonKey);
  });
  return map;
};

// Pick the season to stamp on a catalogue entry: the article's MOST-RECENT real season.
//
// This is the fix for the cross-season blank-catalogue bug. The picker used to stamp the
// browsing/catalogue season onto every pick, so a product picked from another season got a
// seasonKey it has no `${seasonKey}_collectionKeys` for — and the public viewer silently drops it
// (CatalogueViewer buckets each article strictly by its stamped season, then filters the schema by
// that season's keys). Stamping the article's own most-recent real season guarantees the per-season
// fields exist, so it always renders.
//
// `allSeasons` is chronological ascending (Firestore order); newest = highest index. `fallbackSeasonKey`
// is only used when the article carries no usable seasons at all (never for the normal case).
export const pickStampSeason = (article, allSeasons, fallbackSeasonKey = "") => {
  const seasons = Array.isArray(article?.seasons) ? article.seasons : [];
  if (seasons.length === 0) return fallbackSeasonKey;

  const order = (allSeasons || []).map((s) => s.key);
  let best = null;
  let bestIdx = -1;
  seasons.forEach((s) => {
    const idx = order.indexOf(s);
    if (idx > bestIdx) {
      bestIdx = idx;
      best = s;
    }
  });

  // If none of the article's seasons appear in allSeasons (shouldn't happen in practice), keep a
  // real season of the record (last listed) rather than the browsing-season fallback — the stamp
  // must stay one of the article's own seasons so the viewer's per-season fields resolve.
  return best ?? seasons[seasons.length - 1] ?? fallbackSeasonKey;
};

// Build the {id, seasonKey} entry for a picked record.
//
// `explicitSeasonKey` is the season the rep actually chose (a chip). It wins over pickStampSeason,
// because a carryover record carries several seasons and pickStampSeason always returns the newest
// — which would silently override the choice. It is honoured only if the record really carries that
// season, preserving Layer A's invariant that a stamp must resolve to real per-season fields.
export const buildCatalogueEntry = (
  record,
  allSeasons,
  explicitSeasonKey,
  fallbackSeasonKey = ""
) => {
  const seasons = Array.isArray(record?.seasons) ? record.seasons : [];
  const seasonKey =
    explicitSeasonKey && seasons.includes(explicitSeasonKey)
      ? explicitSeasonKey
      : pickStampSeason(record, allSeasons, fallbackSeasonKey);

  return { id: record.id, seasonKey };
};
